// Busca e monta o resultado do mês da barra "Meu Trabalho" (ver resultadoMes.ts).
//
// São duas consultas pequenas, independentes do funil ativo — o resultado do
// vendedor é dele no mês inteiro, não só do funil que está na tela:
//   1. negociações com termo e mexidas no mês (ou assinadas no mês) → em assinatura,
//      assinados e, pelo status de hoje, validação/reprovados/aprovados;
//   2. negociações com análise cadastral aprovada no mês → base da comissão.
// `ultima_atividade_at` é carimbado pelo banco a cada troca de status/etapa, então todo
// termo enviado no mês tem a negociação "mexida" no mês — o corte não perde ninguém.
import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../../../src/lib/supabase';
import { embedToArray } from '../../../src/lib/utils';
import { APPROVED_DECISIONS } from '../../../src/lib/fechamentoMensal';
import { getTarifaVigente } from '../../../src/lib/comissaoUtils';
import { avaliarSla } from '../../../src/lib/politicaComercial';
import { coletarSlaVendedor } from '../../../src/lib/comissaoExternaV2';
import { buscarPosVendaDasNegociacoes, IndicacoesIndisponiveisError } from '../../../src/lib/comissaoIndicacaoPosVendaDb';
import { calcularBonificacaoDasVendas } from '../../../src/lib/bonificacaoParceiroFolha';
import type { BonificacaoDaVenda } from '../../../src/lib/bonificacaoParceiroFolha';
import type { Profile } from '../../../types';
import {
    janelaDoMes,
    aprovadaNoMes,
    mesclarLinhas,
    apurarResultadoMes,
    resultadoVazio,
    politicaDoPerfil,
    calcularComissaoGarantida,
    metaVigente,
    somarContagens,
    somarComissoes,
} from './resultadoMes';
import type {
    JanelaMes,
    LinhaResultadoMes,
    ResultadoResponsavel,
    PoliticaComissao,
    ComissaoGarantida,
    ParametrosComissao,
    Contagem,
} from './resultadoMes';

export interface PessoaResultadoMes extends ResultadoResponsavel {
    nome: string;
    politica: PoliticaComissao | null;
    comissao: ComissaoGarantida | null;
}

/** Soma do recorte — mesmas etapas de ResultadoResponsavel (ver resultadoMes.ts). */
export interface TotalResultadoMes {
    emAssinatura: Contagem;
    assinados: Contagem;
    validacao: Contagem;
    pendencia: Contagem;
    reprovados: Contagem;
    aprovados: Contagem;
    saidos: Contagem;
    /** Aprovações do mês de termo assinado antes do mês — entram na comissão, não em "aprovados". */
    aprovadasDeOutroMes: number;
    comissao: number | null;
}

export interface EstadoResultadoMes {
    carregando: boolean;
    erro: string | null;
    janela: JanelaMes;
    pessoas: PessoaResultadoMes[];
    total: TotalResultadoMes;
    /** Alguém do recorte tem comissão de vendedor? (senão a barra mostra "—" sem alarde) */
    temComissao: boolean;
    /** Momento da última carga completa; null até a primeira (a tela mostra o esqueleto). */
    atualizadoEm: number | null;
}

interface Opcoes {
    /** Responsáveis escolhidos no filtro da tela (vazio = todos os que o papel enxerga). */
    selecionados: string[];
    /** Recorte do papel — o mesmo do board. `null` = vê todos. */
    escopoResponsaveis: () => Promise<string[] | null>;
    perfis: Profile[];
    /** Mesmos cortes por papel do board (ocultar parceiros, gerente de parcerias...). */
    responsavelVisivel: (id: string) => boolean;
    ativo: boolean;
    /** Mês a apurar, 'AAAA-MM' (padrão: o mês atual). A tela inicial da Gestão Comercial escolhe o mês. */
    mes?: string;
}

const PAGINA = 1000;
/** Com até este tanto de pessoas escolhidas, quem não teve evento aparece zerado (e com a comissão R$ 0,00). */
const MAX_PESSOAS_ZERADAS = 20;
/** Ao voltar para a aba, só recarrega se a última carga tiver mais que isto. */
const RECARGA_FOCO_MS = 60_000;

// Valores com ':' e '.' precisam de aspas dentro de or() do PostgREST.
const q = (iso: string) => `"${iso}"`;

async function paginar<T>(montar: () => any): Promise<T[]> {
    const todas: T[] = [];
    for (let pagina = 0; ; pagina++) {
        const de = pagina * PAGINA;
        const { data, error } = await montar().order('id', { ascending: true }).range(de, de + PAGINA - 1);
        if (error) throw error;
        const lote = (data || []) as T[];
        todas.push(...lote);
        if (lote.length < PAGINA) break;
    }
    return todas;
}

/** Negociações com análise cadastral aprovada no mês (base da comissão), com os campos do plano. */
async function buscarAprovadasDoMes(ids: string[] | null, janela: JanelaMes): Promise<LinhaResultadoMes[]> {
    const ini = janela.inicio.toISOString();
    const fim = janela.fimExclusivo.toISOString();
    const aprovadas = await paginar<LinhaResultadoMes>(() => {
        const query = supabase
            .from('negociacoes')
            .select(`id, responsavel_id, status, consumo_medio_kwh, created_at, data_assinatura_termo, fidelidade, origem, plano_codigo, publico, multiplicador_final, cpf_cnpj,
                unidades_consumidoras!unidades_consumidoras_negociacao_id_fkey(numero_uc, grupo_tarifario, desconto_tarifa, desconto_bandeira),
                analise_cadastral!analise_cadastral_negociacao_id_fkey!inner(decisao_final, decisao_final_at)`)
            .in('analise_cadastral.decisao_final', APPROVED_DECISIONS)
            .gte('analise_cadastral.decisao_final_at', ini)
            .lt('analise_cadastral.decisao_final_at', fim);
        return ids ? query.in('responsavel_id', ids) : query;
    });
    // O embed 1→1 vem como objeto; a regra de aprovação lê como lista.
    aprovadas.forEach((n: any) => { n.analise_cadastral = embedToArray(n.analise_cadastral); });
    return aprovadas;
}

async function buscarLinhasDoMes(ids: string[] | null, janela: JanelaMes): Promise<LinhaResultadoMes[]> {
    const ini = janela.inicio.toISOString();
    const recorte = (query: any) => (ids ? query.in('responsavel_id', ids) : query);

    const [termos, aprovadas] = await Promise.all([
        paginar<LinhaResultadoMes>(() => recorte(
            supabase
                .from('negociacoes')
                .select('id, responsavel_id, status, consumo_medio_kwh, created_at, data_assinatura_termo, url_termo')
                .or(`data_assinatura_termo.gte.${q(ini)},and(url_termo.not.is.null,or(ultima_atividade_at.gte.${q(ini)},updated_at.gte.${q(ini)}))`),
        )),
        buscarAprovadasDoMes(ids, janela),
    ]);

    return mesclarLinhas(termos, aprovadas);
}

/**
 * Bonificação da regra de 26/10/2026 dos parceiros do recorte. A faixa é do mês
 * inteiro do parceiro e soma as vendas da equipe (vendedor_parceiro com
 * parceiro_id dele), que podem estar fora do filtro da tela — essas são buscadas
 * à parte, só para a faixa.
 */
async function bonificacaoDosParceiros(
    parceiroIds: string[],
    porResp: Map<string, ResultadoResponsavel>,
    idsCarregados: string[] | null,
    perfis: Profile[],
    janela: JanelaMes,
): Promise<Map<number, BonificacaoDaVenda>> {
    if (parceiroIds.length === 0) return new Map();
    const donos = new Set(parceiroIds);
    const equipe = perfis.filter(p => p.role === 'vendedor_parceiro' && p.parceiro_id && donos.has(p.parceiro_id));
    const carregado = (id: string) => idsCarregados === null || idsCarregados.includes(id);
    const faltam = equipe.filter(p => !carregado(p.id)).map(p => p.id);
    const deFora = faltam.length > 0
        ? (await buscarAprovadasDoMes(faltam, janela)).filter(l => aprovadaNoMes(l, janela))
        : [];

    const donoDe = new Map<string, string>([
        ...parceiroIds.map(id => [id, id] as [string, string]),
        ...equipe.map(p => [p.id, p.parceiro_id!] as [string, string]),
    ]);
    const linhas = [
        ...[...parceiroIds, ...equipe.filter(p => carregado(p.id)).map(p => p.id)].flatMap(id => porResp.get(id)?.aprovadas || []),
        ...deFora,
    ];
    return calcularBonificacaoDasVendas(linhas
        .filter(l => l.responsavel_id && donoDe.has(l.responsavel_id))
        .map(l => ({
            negociacao_id: Number(l.id),
            dono_id: donoDe.get(l.responsavel_id!)!,
            decisao_final_at: l.analise_cadastral?.[0]?.decisao_final_at || '',
            consumo_medio_kwh: l.consumo_medio_kwh,
            cpf_cnpj: l.cpf_cnpj,
            ucs: l.unidades_consumidoras,
        })));
}

/**
 * Tarifa e meta de cada pessoa. A função do banco entrega isso para qualquer papel
 * interno (só a própria linha para quem não é gestor). Se ela ainda não existir no
 * banco, lê direto das tabelas — o que a RLS só permite para adm/gerente/analista.
 */
async function buscarParametros(ids: string[], janela: JanelaMes): Promise<Map<string, ParametrosComissao>> {
    const out = new Map<string, ParametrosComissao>();
    if (ids.length === 0) return out;

    const { data, error } = await supabase.rpc('parametros_comissao_mes', { p_vendedores: ids, p_mes: janela.mes, p_ano: janela.ano });
    if (!error) {
        for (const r of (data || []) as Array<{ vendedor_id: string; meta_kwh: number | string | null; tarifa_valor: number | string | null }>) {
            out.set(r.vendedor_id, {
                metaKwh: Number(r.meta_kwh) || 0,
                tarifa: r.tarifa_valor == null ? null : Number(r.tarifa_valor),
            });
        }
        return out;
    }

    const mm = String(janela.mes).padStart(2, '0');
    const [tarifasRes, registrosRes] = await Promise.all([
        supabase.from('tarifas_energia').select('*'),
        supabase.from('comissao_vendedores').select('vendedor_id, mes, ano, meta_kwh, salario_base').in('vendedor_id', ids).lte('ano', janela.ano),
    ]);
    const tarifas = (tarifasRes.data || []) as any[];
    // Sem acesso às tabelas (RLS): sem parâmetro — a barra mostra "—" em vez de inventar.
    if (tarifasRes.error || registrosRes.error || tarifas.length === 0) return out;
    const tarifa = getTarifaVigente(tarifas, `${janela.ano}-${mm}-01`);
    for (const id of ids) {
        out.set(id, {
            tarifa: tarifa ? Number(tarifa.valor) : null,
            metaKwh: metaVigente((registrosRes.data || []) as any[], id, janela.mes, janela.ano),
        });
    }
    return out;
}

const estadoInicial = (): EstadoResultadoMes => ({
    carregando: true,
    erro: null,
    janela: janelaDoMes(),
    pessoas: [],
    total: {
        emAssinatura: { qtd: 0, kwh: 0 }, assinados: { qtd: 0, kwh: 0 }, validacao: { qtd: 0, kwh: 0 },
        pendencia: { qtd: 0, kwh: 0 }, reprovados: { qtd: 0, kwh: 0 }, aprovados: { qtd: 0, kwh: 0 }, saidos: { qtd: 0, kwh: 0 },
        aprovadasDeOutroMes: 0, comissao: null,
    },
    temComissao: false,
    atualizadoEm: null,
});

/** 'AAAA-MM' → um dia dentro do mês; vazio ou inválido → hoje. */
const referenciaDoMes = (mes: string | undefined): Date => {
    const m = /^(\d{4})-(\d{2})$/.exec(mes ?? '');
    return m ? new Date(Number(m[1]), Number(m[2]) - 1, 1) : new Date();
};

export function useResultadoMes({ selecionados, escopoResponsaveis, perfis, responsavelVisivel, ativo, mes }: Opcoes): EstadoResultadoMes {
    const [estado, setEstado] = useState<EstadoResultadoMes>(estadoInicial);
    const requisicao = useRef(0);
    const ultimaCarga = useRef(0);

    // Ref: a carga lê sempre a lista atual. `responsavelVisivel` é dependência de
    // propósito — muda quando o filtro por papel muda (ex.: ocultar parceiros) e
    // quando os perfis chegam, e nos dois casos o resultado precisa ser refeito.
    const perfisRef = useRef(perfis);
    perfisRef.current = perfis;

    const selecionadosKey = JSON.stringify([...selecionados].sort());

    const carregar = useCallback(async () => {
        const minha = ++requisicao.current;
        ultimaCarga.current = Date.now();
        const janela = janelaDoMes(referenciaDoMes(mes));
        setEstado(prev => ({ ...prev, carregando: true, erro: null, janela }));
        try {
            const escopo = await escopoResponsaveis();
            const escolhidos: string[] = JSON.parse(selecionadosKey);
            const ids: string[] | null = escolhidos.length > 0
                ? (escopo ? escolhidos.filter(id => escopo.includes(id)) : escolhidos)
                : escopo;

            const linhas = ids && ids.length === 0 ? [] : await buscarLinhasDoMes(ids, janela);
            if (minha !== requisicao.current) return;

            const porResp = apurarResultadoMes(linhas, janela);
            // Pessoas escolhidas explicitamente aparecem mesmo zeradas (ex.: "Eu" sem nada ainda no mês).
            if (ids && ids.length <= MAX_PESSOAS_ZERADAS) {
                for (const id of ids) if (!porResp.has(id)) porResp.set(id, resultadoVazio(id));
            }

            const perfilPorId = new Map(perfisRef.current.map(p => [p.id, p]));
            const pessoasBase = [...porResp.values()]
                .filter(r => responsavelVisivel(r.responsavelId))
                .map(r => {
                    const perfil = perfilPorId.get(r.responsavelId);
                    return {
                        ...r,
                        nome: perfil?.full_name || 'Sem nome',
                        politica: politicaDoPerfil(perfil, janela.mes, janela.ano),
                    };
                });

            const comPolitica = pessoasBase.filter(p => p.politica);
            const [params, bonificacao] = await Promise.all([
                buscarParametros(comPolitica.map(p => p.responsavelId), janela),
                bonificacaoDosParceiros(
                    comPolitica.filter(p => p.politica === 'parceiro').map(p => p.responsavelId),
                    porResp, ids, perfisRef.current, janela,
                ),
            ]);

            // Vendas indicadas pelo pós-venda (#6236): a comissão de quem vendeu fica com 60%.
            // Sem conseguir ler as indicações, quem tem aprovada fica com "—" em vez de um valor
            // que pode estar a mais.
            let indicacoesConfiaveis = true;
            try {
                const { porNegociacao } = await buscarPosVendaDasNegociacoes(comPolitica.flatMap(p => p.aprovadas));
                for (const p of comPolitica) for (const a of p.aprovadas) a.pos_venda_id = porNegociacao.get(Number(a.id)) ?? null;
            } catch (e) {
                if (!(e instanceof IndicacoesIndisponiveisError)) throw e;
                indicacoesConfiaveis = false;
            }

            // SLA do vendedor externo só pesa se ele já tem aprovada no mês. Rede de
            // parceiros fica fora (passa 0) — conservador, como o resto do "garantido".
            const slaPorId = new Map<string, boolean>();
            await Promise.all(comPolitica
                .filter(p => p.politica === 'externo_v2' && p.aprovadas.length > 0)
                .map(async p => {
                    try {
                        const d = await coletarSlaVendedor(p.responsavelId, janela.mes, janela.ano, 0);
                        slaPorId.set(p.responsavelId, avaliarSla(d).cumprido);
                    } catch { /* SLA desconhecido = não cumprido (conservador) */ }
                }));
            if (minha !== requisicao.current) return;

            const pessoas: PessoaResultadoMes[] = pessoasBase
                .map(p => {
                    const prm = params.get(p.responsavelId);
                    const semIndicacoes = !indicacoesConfiaveis && p.politica !== 'parceiro' && p.aprovadas.length > 0;
                    const comissao = p.politica && prm && !semIndicacoes
                        ? calcularComissaoGarantida(p.politica, p.aprovadas, prm, { slaCumprido: slaPorId.get(p.responsavelId), bonificacao })
                        : null;
                    return { ...p, comissao };
                })
                .sort((a, b) =>
                    (b.aprovados.kwh - a.aprovados.kwh) ||
                    (b.assinados.kwh - a.assinados.kwh) ||
                    (b.emAssinatura.kwh - a.emAssinatura.kwh) ||
                    a.nome.localeCompare(b.nome, 'pt-BR'));

            setEstado({
                carregando: false,
                erro: null,
                janela,
                pessoas,
                total: {
                    emAssinatura: somarContagens(pessoas.map(p => p.emAssinatura)),
                    assinados: somarContagens(pessoas.map(p => p.assinados)),
                    validacao: somarContagens(pessoas.map(p => p.validacao)),
                    pendencia: somarContagens(pessoas.map(p => p.pendencia)),
                    reprovados: somarContagens(pessoas.map(p => p.reprovados)),
                    aprovados: somarContagens(pessoas.map(p => p.aprovados)),
                    saidos: somarContagens(pessoas.map(p => p.saidos)),
                    aprovadasDeOutroMes: pessoas.reduce((s, p) => s + p.aprovadasDeOutroMes, 0),
                    comissao: somarComissoes(pessoas),
                },
                temComissao: pessoas.some(p => p.politica),
                atualizadoEm: Date.now(),
            });
        } catch (e: any) {
            if (minha !== requisicao.current) return;
            console.error('Resultado do mês:', e);
            setEstado(prev => ({ ...prev, carregando: false, erro: 'Não foi possível carregar o resultado do mês.' }));
        }
    }, [escopoResponsaveis, selecionadosKey, responsavelVisivel, mes]);

    // Recarrega quando o filtro muda (com folga para cliques seguidos) e quando os
    // perfis chegam — sem eles não dá para saber quem tem comissão.
    useEffect(() => {
        if (!ativo) return;
        const t = setTimeout(carregar, 300);
        return () => clearTimeout(t);
    }, [ativo, carregar]);

    // Ações que mexem em negociação (modais, detalhe) disparam refreshKanban; e quem
    // volta para a aba depois de aprovar algo na Gestão vê o número novo.
    useEffect(() => {
        if (!ativo) return;
        const aoRefresh = () => carregar();
        const aoVoltar = () => {
            if (document.visibilityState === 'visible' && Date.now() - ultimaCarga.current > RECARGA_FOCO_MS) carregar();
        };
        window.addEventListener('refreshKanban', aoRefresh);
        document.addEventListener('visibilitychange', aoVoltar);
        return () => {
            window.removeEventListener('refreshKanban', aoRefresh);
            document.removeEventListener('visibilitychange', aoVoltar);
        };
    }, [ativo, carregar]);

    return estado;
}
